import { hexToWorld } from '../hex.ts';
import { idiv, withinRange } from '../math/fixed.ts';
import type { BattleState, Structure } from '../types.ts';
import { firesAs } from './towers.ts';
import { turnedOnHeroes } from './vigil.ts';

/*
 * A bastion wave under frost (docs/PRESAGIOS.md §3.3): the towers reload slower, but those a charged burning-oil
 * trap keeps warm. Each tower is marked cold or warm once, when the fight starts (`Structure.cold`), and an oil that
 * warms a tower burns one charge to light. What reloads asks the mark, nothing per nightmare.
 */

/** What the frost slows: what fires as a tower and reloads (also a wall with a tower on it, and the belfry). */
export const frostable = (st: Structure): boolean => {
  const f = firesAs(st);
  return !!f && f.def.type !== 'beacon' && st.type !== 'titanEye';
};

/** Whether a burning-oil trap reaches this tower with its warmth (anchor cell to anchor cell). */
const warms = (oil: Structure, st: Structure, range: number): boolean => {
  const a = hexToWorld(oil.q, oil.r);
  const b = hexToWorld(st.q, st.r);
  return withinRange(a.x, a.y, b.x, b.y, range);
};

/**
 * When the fight starts: the oils, in cell order, light where a tower would be slowed, one charge each, and warm
 * every tower they reach; a fire-enchanted tower is warm by itself. The rest are marked cold. One that already
 * reaches only warm towers doesn't light, so two oils by the same towers burn one charge.
 */
export const markFrost = (s: BattleState): void => {
  const range = s.rules?.warmRange;
  if (!s.rules?.towerReloadBp || range === undefined) return;
  const towers = s.structures.filter((st) => !st.destroyed && frostable(st));
  if (!towers.length) return;
  const warm = new Set<number>();
  for (const st of towers) if (st.enchant === 'fire') warm.add(st.id);
  for (const oil of s.structures) {
    if (oil.type !== 'oil' || oil.destroyed || oil.charges <= 0) continue;
    const near = towers.filter((st) => warms(oil, st, range));
    if (!near.some((st) => !warm.has(st.id))) continue;
    oil.charges--;
    oil.lit = true;
    for (const st of near) warm.add(st.id);
  }
  for (const st of towers) if (!warm.has(st.id)) st.cold = true;
  s.stats.sky = { warm: warm.size, towers: towers.length };
};

/**
 * A tower raised mid-fight (only in practice, with a purse): marked by the oils with a charge left, burning none. An
 * oil laid mid-fight doesn't warm the towers already there (the marks are never redone).
 */
export const markRaised = (s: BattleState, st: Structure): void => {
  const range = s.rules?.warmRange;
  if (!s.rules?.towerReloadBp || range === undefined || !frostable(st)) return;
  if (st.enchant === 'fire' || s.structures.some((o) => o.type === 'oil' && !o.destroyed && o.charges > 0 && warms(o, st, range))) return;
  st.cold = true;
};

/**
 * A tower's reload: `base` slowed by `chill` (the strongest colossus near it) and its branch's `branchBp`, truncated
 * twice, as always. A cold tower on a frost wave counts it as one product with the stronger of the frost and the
 * colossus, rounded up once, so it never reloads sooner than without the frost; it keeps the ticks the frost added
 * (`frostCd`). Turned on the heroes it shoots with no frost (Bruni takes back what the frost added, bruni.ts).
 */
export const frostReload = (s: BattleState, st: Structure, base: number, chill: number, branchBp: number): number => {
  const clear = idiv(idiv(base * chill, 10000) * branchBp, 10000);
  const bp = s.rules?.towerReloadBp;
  if (!bp || !st.cold) return clear;
  if (turnedOnHeroes(s, st)) {
    st.frostCd = 0;
    return clear;
  }
  const cold = chill > bp ? chill : bp;
  const r = idiv(base * cold * branchBp + 99_999_999, 100_000_000);
  st.frostCd = r - clear;
  return r;
};
